import { Alert, FormGroup } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ICheckedTreatment } from '@common/interfaces';
import type { TherapielandProgramSelectionFields } from '@common/validation/customControls/osas/osasConsultSchemas/types';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils';
import { CheckMarkButton } from '@frontend/components/elements/buttons/checkMarkButton/CheckMarkButton';
import { ButtonContainer, FormParagraph, IconOnClick } from '@frontend/components/elements/common';
import { TherapielandProgrammasModal } from '@frontend/components/elements/TherapielandProgrammasModal';
import { hasActiveGgzbTherServiceConsumerRights } from '@frontend/services/api/serviceConsumerApiService';
import { isEmailDuplicateForPatient } from '@frontend/services/api/therapielandApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';

type TherapielandProgrammesProps = {
    checkedTreatments: ICheckedTreatment[];
    errors: ValidationErrors;
    onChange: (data: Partial<TherapielandProgramSelectionFields>, validateKeys?: string | string[] | undefined) => void;
};

export function TherapielandProgrammesEdit(props: Readonly<TherapielandProgrammesProps>): JSX.Element | null {
    const { checkedTreatments, onChange, errors } = props;
    const translate = getSelectedLanguage();
    const abortSignal = useAbortSignal();
    const { patient } = usePatientEpisodeCategoryContext();

    const employmentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [hasMentalHealthRights, setHasMentalHealthRights] = useState<boolean>();
    const [isDuplicateEmailAddress, setIsDuplicateEmailAddress] = useState<boolean>();

    useEffect(() => {
        getMentalHealthRights();
    }, []);

    useEffect(() => {
        checkDuplicateEmailAddress();
    }, [patient.emailAddress]);

    return (
        <>
            {isModalOpen && (
                <TherapielandProgrammasModal
                    checkedTreatments={checkedTreatments}
                    handleChange={async (values: ICheckedTreatment[]) => {
                        setIsModalOpen(false);
                        onChange({ checkedTreatments: values }, 'checkedTreatments');
                    }}
                    handleClose={async () => {
                        setIsModalOpen(false);
                    }}
                    isOpen={isModalOpen}
                    patientId={patient.id}
                />
            )}

            <FormGroup
                hasErrors={errors.has('checkedTreatments') || isDuplicateEmailAddress}
                label="E-health programma´s"
            >
                <>
                    {checkedTreatments.map((treatment: ICheckedTreatment) => {
                        return (
                            <FormParagraph
                                bold
                                key={treatment.id}
                            >
                                {treatment.treatmentName + ' '}
                                <IconOnClick
                                    icon={'delete'}
                                    onClick={async () => {
                                        const updatedTreatments = checkedTreatments?.filter(
                                            (checkedTreatment: ICheckedTreatment) =>
                                                checkedTreatment.id !== treatment.id
                                        );
                                        onChange({ checkedTreatments: updatedTreatments }, 'checkedTreatments');
                                    }}
                                />
                            </FormParagraph>
                        );
                    })}

                    <ButtonContainer
                        buttons={[
                            <CheckMarkButton
                                disabled={hasMentalHealthRights === false || isDuplicateEmailAddress}
                                isChecked={false}
                                key={'selectTherapielandProgramme'}
                                onClick={() => setIsModalOpen(true)}
                            >
                                {translate.diagnosticReport.selectTherapyLandProgramme}
                            </CheckMarkButton>,
                        ]}
                        noMargin
                        wrapContent
                    />
                </>
            </FormGroup>

            <>
                {hasMentalHealthRights === false && (
                    <Alert appearance="warning">
                        Je hebt geen rechten voor het aanvragen van de programma´s. Neem contact op met de servicedesk.
                    </Alert>
                )}

                {isDuplicateEmailAddress && (
                    <Alert appearance="warning">
                        Het e-mailadres van de patiënt wordt al door iemand anders gebruikt om programma´s van
                        Therapieland te ontvangen. Pas het aan als je toch een programma wil aanvragen.
                    </Alert>
                )}
            </>
        </>
    );

    async function getMentalHealthRights() {
        setHasMentalHealthRights(await hasActiveGgzbTherServiceConsumerRights(employmentId, abortSignal));
    }

    async function checkDuplicateEmailAddress() {
        setIsDuplicateEmailAddress(
            await isEmailDuplicateForPatient(patient.emailAddress ?? null, patient.id, abortSignal)
        );
    }
}
